import { useEffect, useState } from 'react';

import { brazilianRealFormat } from 'app/utils';
import { useResources } from 'features/resources';
import { useTracking } from 'features/tracking';
import { Flex, Icons, Typography } from 'components';
import EventButton from '../eventButton';
import EventInteressedButton from '../eventInteressedButton/EventInteressedButton';
import { useProducts } from 'features/products';
import styled from 'styled-components';

import EventConfirmation from '../eventConfirmation';
import { LiveMeetingAttendanceConfirmationStatusEnum } from 'app/types/cards/LiveVideoCard';
import { EventStatusEnum } from 'features/events/types';

interface EventsEnrollmentBoxProps {
  apiFetch: (forceEnroll: boolean) => void;
  paymentLinkUrl: string | false | null;
}

const BadgeButtonIcon = styled(Icons)`
  width: 24px;
  color: white;
`;

const BadgeButtonIconPrimary = styled(Icons)`
  width: 24px;
  color: ${({ theme }) => theme.colors.primary_400};
`;

const BadgeButtonIconGray = styled(Icons)`
  width: 24px;
  color: ${({ theme }) => theme.colors.gray_500};
`;

const EventsEnrollmentBox = ({ paymentLinkUrl, apiFetch }: EventsEnrollmentBoxProps) => {
  const { productDetails, availableClasses, clearAvailableClasses } = useProducts();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    schools: { schoolMPC },
  } = useResources();

  const { eventTracker } = useTracking();

  useEffect(() => {
    return () => {
      clearAvailableClasses();
    };
  }, []);

  return (
    <>
      <EventConfirmation
        openEventConfirmation={showConfirmation}
        onclose={(value) => setShowConfirmation(value)}
        checkinLiveMeetingUuid={productDetails?.event?.checkinLiveMeetingUuid}
        confirmationStatus={productDetails?.event?.attendanceConfirmationStatus}
      />
      <Flex flexDirection="column" width={400}>
        {productDetails?.event?.eventStatus === EventStatusEnum.Enrollment && (
          <Flex bg="dark_350" flexDirection="row" px={{ m: 4, d: 5 }} py={3} alignItems="center">
            <BadgeButtonIconPrimary icon="calendarCheckOutline" />
            <Typography type="type2" lineHeight="1.3" color="primary_400" upper ml={2} fontWeight="bold">
              Inscrições abertas
            </Typography>
          </Flex>
        )}

        {productDetails?.event?.eventStatus === EventStatusEnum.New && (
          <Flex bg="dark_350" flexDirection="row" px={{ m: 4, d: 5 }} py={3} alignItems="center">
            <BadgeButtonIconGray icon="calendarCheckOutline" />
            <Typography type="type2" lineHeight="1.3" color="gray_500" upper ml={2} fontWeight="bold">
              Em Breve
            </Typography>
          </Flex>
        )}

        {productDetails?.event?.eventStatus === EventStatusEnum.Closed && (
          <Flex bg="dark_350" flexDirection="row" px={{ m: 4, d: 5 }} py={3} alignItems="center">
            <BadgeButtonIconGray icon="closeOutline" />
            <Typography type="type2" lineHeight="1.3" color="gray_500" upper ml={2} fontWeight="bold">
              Evento Finalizado
            </Typography>
          </Flex>
        )}

        {productDetails?.enrolled && productDetails?.event?.eventStatus !== EventStatusEnum.Closed && (
          <Flex bg="dark_gray" flexDirection="row" px={{ m: 4, d: 5 }} py={3} alignItems="center" justifyContent="space-between">
            <Typography type="type2" lineHeight="1.3" color="white" upper>
              Sua inscrição está confirmada
            </Typography>
            <BadgeButtonIcon icon="checkCircleOutline" />
          </Flex>
        )}

        {productDetails?.event?.eventStatus === EventStatusEnum.Open && !productDetails?.enrolled && (
          <Flex bg="dark_gray" flexDirection="row" px={{ m: 4, d: 5 }} py={3} alignItems="center" justifyContent="space-between">
            <Typography type="type2" lineHeight="1.3" color="white" upper>
              O evento já começou
            </Typography>
            <BadgeButtonIcon icon="radiotower" />
          </Flex>
        )}

        {availableClasses?.current?.installment && availableClasses?.current?.price ? (
          <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="dark_350">
            <Typography type="type2" lineHeight="1.3" color="white" upper>
              Investimento
            </Typography>
            <Typography as="span" type="type5" lineHeight="1.1" color="white" fontWeight="bold" mt={2}>
              {availableClasses?.current?.installment}x {brazilianRealFormat(availableClasses?.current?.installmentValue)}
            </Typography>
            <Typography as="span" type="type1" lineHeight="1.3" color="white" mt={2}>
              ou {brazilianRealFormat(availableClasses?.current?.price)} a vista
            </Typography>
          </Flex>
        ) : null}

        <>
          {!productDetails?.enrolled && productDetails?.allowOnDemandEnrollments ? (
            <EventButton onClick={() => apiFetch(true)} label="Matricular agora" type="nolink" to="" borderRadiusTop />
          ) : null}
        </>

        {productDetails?.event?.eventStatus === EventStatusEnum.Enrollment && paymentLinkUrl ? (
          <EventButton
            onClick={() => {
              if (schoolMPC) {
                eventTracker({ category: 'checkout', action: 'Add to Cart', label: productDetails?.product?.name });
              }
            }}
            label="Fazer inscrição"
            type="external"
            to={paymentLinkUrl}
            target="_blank"
          />
        ) : null}

        {productDetails?.enrolled &&
          productDetails?.event?.eventStatus === EventStatusEnum.Checkin &&
          productDetails?.event?.attendanceConfirmationStatus !== LiveMeetingAttendanceConfirmationStatusEnum.Confirmed && (
            <EventButton
              onClick={() => setShowConfirmation(!showConfirmation)}
              label="Confirme sua presença"
              type="nolink"
              icon="clipboardCheck"
              to={''}
              target="_blank"
            />
          )}

        {productDetails?.enrolled &&
          productDetails?.event?.eventStatus === EventStatusEnum.Checkin &&
          productDetails?.event?.attendanceConfirmationStatus === LiveMeetingAttendanceConfirmationStatusEnum.Confirmed && (
            <EventButton label="Presença confirmada" type="nolink" icon="clipboardCheck" to={''} target="_blank" />
          )}

        {!productDetails?.enrolled && productDetails?.event?.allowInterestList && !availableClasses?.current?.allowEnrollments && !paymentLinkUrl && (
          <EventInteressedButton label={`Tenho interesse nesse Evento`} />
        )}
      </Flex>
    </>
  );
};

export default EventsEnrollmentBox;
